import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, Twitter, Mail, Phone, MapPin } from "lucide-react";
import { useSettings } from "@/lib/settings";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATIC_ROW1 = [
  { label: "Internships", to: "/internship" },
  { label: "Placement", to: "/placement" },
  { label: "Certification", to: "/certification" },
  { label: "Corporate Training", to: "/corporate-training" },
];

export default function Footer() {
  const { settings } = useSettings();
  const [email, setEmail] = useState("");
  const [redirects, setRedirects] = useState([]);

  useEffect(() => {
    api.get("/redirects").then(({ data }) => setRedirects(data)).catch(() => {});
  }, []);

  if (!settings) return null;
  const { contact = {}, social = {}, footer = {}, student_portal = {}, mobile_app = {}, branding = {} } = settings;

  const subscribe = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email }); toast.success("Subscribed!"); setEmail(""); }
    catch { toast.error("Try again"); }
  };

  const footerLinks = redirects.filter((r) => r.enabled && r.show_in_footer);
  const row1 = footerLinks.filter((r) => (r.footer_row || 1) === 1).sort((a, b) => (a.footer_order || 0) - (b.footer_order || 0));
  const row2 = footerLinks.filter((r) => (r.footer_row || 1) === 2).sort((a, b) => (a.footer_order || 0) - (b.footer_order || 0));

  const renderLink = (l) => (
    <a key={l.id || l.label} href={l.mode === "external" ? l.destination_url : l.source_path}
       target={l.open_new_tab ? "_blank" : undefined} rel={l.open_new_tab ? "noreferrer" : undefined}
       className="hover:text-white transition-colors" data-testid={`footer-link-${l.key || l.label.toLowerCase().replace(/\s/g,'-')}`}>
      {l.label}
    </a>
  );

  return (
    <footer className="bg-neutral-950 text-neutral-300 mt-24" data-testid="site-footer">
      <div className="container-eq py-20">
        <div className="grid md:grid-cols-4 gap-10">
          <div>
            {branding.logo_footer
              ? <img src={branding.logo_footer} alt="EQtech Academy" className="h-12 mb-4" data-testid="footer-logo" />
              : <div className="text-white font-display font-black text-2xl mb-3">EQtech Academy</div>}
            <p className="text-sm text-neutral-400 leading-relaxed">
              AI-powered digital marketing education with live training, real-world projects and career-focused mentorship.
            </p>
            <div className="flex gap-3 mt-5">
              {social.facebook && <a href={social.facebook} target="_blank" rel="noreferrer" data-testid="social-facebook" className="w-9 h-9 rounded-full bg-neutral-800 hover:bg-sky-500 flex items-center justify-center transition-colors"><Facebook className="w-4 h-4" /></a>}
              {social.instagram && <a href={social.instagram} target="_blank" rel="noreferrer" data-testid="social-instagram" className="w-9 h-9 rounded-full bg-neutral-800 hover:bg-sky-500 flex items-center justify-center transition-colors"><Instagram className="w-4 h-4" /></a>}
              {social.linkedin && <a href={social.linkedin} target="_blank" rel="noreferrer" data-testid="social-linkedin" className="w-9 h-9 rounded-full bg-neutral-800 hover:bg-sky-500 flex items-center justify-center transition-colors"><Linkedin className="w-4 h-4" /></a>}
              {social.youtube && <a href={social.youtube} target="_blank" rel="noreferrer" data-testid="social-youtube" className="w-9 h-9 rounded-full bg-neutral-800 hover:bg-sky-500 flex items-center justify-center transition-colors"><Youtube className="w-4 h-4" /></a>}
              {social.twitter && <a href={social.twitter} target="_blank" rel="noreferrer" data-testid="social-twitter" className="w-9 h-9 rounded-full bg-neutral-800 hover:bg-sky-500 flex items-center justify-center transition-colors"><Twitter className="w-4 h-4" /></a>}
            </div>
          </div>

          <div>
            <div className="label-eq text-neutral-500 mb-4">Quick Links</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/courses" className="hover:text-white transition-colors">Courses</Link></li>
              <li><Link to="/trainers" className="hover:text-white transition-colors">Trainers</Link></li>
              <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/gallery" className="hover:text-white transition-colors">Gallery</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div>
            <div className="label-eq text-neutral-500 mb-4">Contact</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 shrink-0" /> {contact.phone}</li>
              <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 shrink-0" /> {contact.email}</li>
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0" /> {contact.address}</li>
            </ul>
            <div className="mt-5 space-y-2 text-sm">
              <a href={student_portal.login} target="_blank" rel="noreferrer" className="block hover:text-white">Student Login →</a>
              <a href={student_portal.signup} target="_blank" rel="noreferrer" className="block hover:text-white">Student Registration →</a>
            </div>
          </div>

          <div>
            <div className="label-eq text-neutral-500 mb-4">Get the App</div>
            <div className="flex flex-col gap-3">
              {mobile_app.google_play_url && (
                <a href={mobile_app.google_play_url} target="_blank" rel="noreferrer" data-testid="footer-google-play" className="inline-block">
                  {mobile_app.google_play_badge ? <img src={mobile_app.google_play_badge} alt="Google Play" className="h-11" /> : <div className="bg-neutral-900 text-white text-xs px-4 py-2 rounded-lg">Google Play</div>}
                </a>
              )}
              {mobile_app.apple_store_url && (
                <a href={mobile_app.apple_store_url} target="_blank" rel="noreferrer" data-testid="footer-apple-store" className="inline-block">
                  {mobile_app.apple_store_badge ? <img src={mobile_app.apple_store_badge} alt="App Store" className="h-11" /> : <div className="bg-neutral-900 text-white text-xs px-4 py-2 rounded-lg">App Store</div>}
                </a>
              )}
            </div>
            <div className="label-eq text-neutral-500 mt-6 mb-3">Newsletter</div>
            <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
                     className="flex-1 bg-neutral-900 border border-neutral-800 rounded-full px-4 py-2 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:border-sky-500" data-testid="newsletter-email" />
              <button type="submit" className="bg-sky-500 hover:bg-sky-400 text-white rounded-full px-4 text-sm font-semibold transition-colors" data-testid="newsletter-submit">Join</button>
            </form>
          </div>
        </div>

        {/* Two-row policy / info links */}
        <div className="mt-14 pt-8 border-t border-neutral-800 space-y-3">
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-400" data-testid="footer-row-1">
            {STATIC_ROW1.map((l) => (
              <Link key={l.to} to={l.to} className="hover:text-white transition-colors">{l.label}</Link>
            ))}
            {row1.map(renderLink)}
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-neutral-500" data-testid="footer-row-2">
            {row2.map(renderLink)}
          </div>
        </div>

        <div className="border-t border-neutral-800 mt-8 pt-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-neutral-500">
          <div>{footer.copyright}</div>
          <a href={footer.powered_by_url} target="_blank" rel="noreferrer" data-testid="powered-by-link" className="hover:text-sky-400 transition-colors">
            {footer.powered_by_text}
          </a>
        </div>
      </div>
    </footer>
  );
}
